import { subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { stateToTzMap } from '@ourbranch/state-to-tz-map';

/**
 * Front-end Rate Control Date hold-window helpers — GitHub issue #32130.
 *
 * rateControlDate (RCD) is the rater's as-of date. Brands may "hold" it for a window
 * instead of forcing it to be today at bind:
 *   - maxStaleDaysToBind (X): default days the RCD may lag today and still bind, and below
 *     which staff-fe should not nag to update.
 *   - underwriterMaxHoldDays (Y): the larger window an underwriter may opt into at bind via
 *     the "Hold Rate Control Date" checkbox.
 * These mirror the backend (charge-policy-step / OfferRaterManager) so the FE shows the
 * same fields and gates bind on the same thresholds. Branch is X=0 / Y=0 (legacy behavior).
 */

export const DEFAULT_RATE_CONTROL_DATE_CONFIG = { maxStaleDaysToBind: 0, underwriterMaxHoldDays: 0 };

/** The brand's rateControlDate config, defaulting to {0,0} (Branch / unconfigured). */
export function getRateControlDateConfig(brandConfig) {
  return brandConfig?.rateControlDate ?? DEFAULT_RATE_CONTROL_DATE_CONFIG;
}

/** Days the RCD may lag today for THIS bind: the underwriter Y window when held, else X. */
export function getBindThresholdDays(config, hold) {
  return hold ? config.underwriterMaxHoldDays : config.maxStaleDaysToBind;
}

/** Show the read-only Rate Control Date field only to underwriters on brands that hold (X > 0). */
export function shouldShowRateControlDateField(canApproveUWReview, config) {
  return Boolean(canApproveUWReview && config.maxStaleDaysToBind > 0);
}

/** Show the "Hold Rate Control Date" checkbox only to underwriters on brands with a Y window. */
export function shouldShowHoldCheckbox(canApproveUWReview, config) {
  return Boolean(canApproveUWReview && config.underwriterMaxHoldDays > 0);
}

/**
 * True when the RCD is more than `maxDaysOld` days behind today in the offer state's
 * timezone. Lenient by design: returns false (not stale) when the RCD or the state's
 * timezone is unknown, matching the existing isStale guard — the backend bind gate is the
 * authoritative check. Compares 'YYYY-MM-DD' strings (which sort chronologically) to avoid
 * timezone-math hazards (see offer-store #30635).
 */
export function isRateControlDateOlderThan({ rateControlDate, state, maxDaysOld, now = new Date() }) {
  const tz = state ? stateToTzMap[state] : null;
  if (!rateControlDate || !tz) {
    return false;
  }
  const cutoffStr = formatInTimeZone(subDays(now, maxDaysOld), tz, 'yyyy-MM-dd');
  return rateControlDate < cutoffStr;
}

/** Billing's business time zone: the backend PaymentsService schedules in America/New_York. */
export const BILLING_TIME_ZONE = 'America/New_York';
// Each per-line prebind schedule on an option, with the quote.global fields the rater built it from.
const PAYMENT_SCHEDULE_LINES = [
  {
    scheduleKey: 'autoSchedule',
    effectiveDate: 'autoEffectiveDate',
    billingDayOfMonth: 'autoBillingDayOfMonth',
    paymentType: 'autoPaymentType',
    megaDownPay: 'autoMegaDownPay'
  },
  {
    scheduleKey: 'homeSchedule',
    effectiveDate: 'homeEffectiveDate',
    billingDayOfMonth: 'homeBillingDayOfMonth',
    paymentType: 'homeownersPaymentType',
    megaDownPay: 'homeMegaDownPay'
  },
  {
    scheduleKey: 'rentersSchedule',
    effectiveDate: 'rentersEffectiveDate',
    billingDayOfMonth: 'rentersBillingDayOfMonth',
    paymentType: 'rentersPaymentType',
    megaDownPay: 'rentersMegaDownPay'
  },
  {
    scheduleKey: 'condoSchedule',
    effectiveDate: 'condoEffectiveDate',
    billingDayOfMonth: 'condoBillingDayOfMonth',
    paymentType: 'condoPaymentType',
    megaDownPay: 'condoMegaDownPay'
  },
  {
    scheduleKey: 'farmSchedule',
    effectiveDate: 'farmEffectiveDate',
    billingDayOfMonth: 'farmBillingDayOfMonth',
    paymentType: 'farmPaymentType',
    megaDownPay: 'farmMegaDownPay'
  }
];
const MONTHLY_PAYMENT_TYPE = 'I';

/**
 * #35629: would a monthly installment schedule computed on `bindDay` differ from the one computed on
 * `ratingDay`? Date-only, so staff-fe and charge-policy-step can both apply it without the billing
 * package. Autobill drafts on the billing day of month, so the two schedules only differ when a
 * billing-day date LATER than the term start falls after the rating day and on or before the bind day
 * — crossing the effective date alone changes nothing, a rating ON a billing day already counts it as
 * used, and the billing day that IS the effective date is the effective-date payment, not a draft.
 * Verified against packages/billing getOfferPayment over a 4,176-combination grid. Quarterly, semi-annual
 * and mega-down-pay schedules (and lines missing their billing inputs) always count as changed.
 */
export function paymentScheduleWouldChange({
  ratingDay,
  bindDay,
  effectiveDate,
  billingDayOfMonth,
  paymentType,
  megaDownPay
}) {
  if (ratingDay === bindDay) {
    return false;
  }
  if (paymentType !== MONTHLY_PAYMENT_TYPE || megaDownPay || !effectiveDate || !billingDayOfMonth) {
    return true;
  }
  const [ratingYear, ratingMonth] = ratingDay.split('-').map(Number);
  const [bindYear, bindMonth] = bindDay.split('-').map(Number);
  const monthsSpanned = (bindYear - ratingYear) * 12 + (bindMonth - ratingMonth);
  for (let offset = 0; offset <= monthsSpanned; offset += 1) {
    const monthIndex = ratingMonth - 1 + offset;
    const year = ratingYear + Math.floor(monthIndex / 12);
    const month = String((monthIndex % 12) + 1).padStart(2, '0');
    const billingDate = `${year}-${month}-${String(billingDayOfMonth).padStart(2, '0')}`;
    if (billingDate > effectiveDate && billingDate > ratingDay && billingDate <= bindDay) {
      return true;
    }
  }
  return false;
}

/**
 * #35629: the date an option's prebind installment schedule was computed on, when re-computing it on
 * today's date would change the installments (paymentScheduleWouldChange) — else null. The rater computes the schedule on the RATING day and autobill schedules from the
 * BIND day, so a mutual-brand offer (maxStaleDaysToBind > 0) bound days after it was rated would
 * authorize one schedule and draft another. Within maxStaleDaysToBind an Update re-rate keeps the rate
 * control date (price-neutral) and refreshes the schedule, so checkout requires it. Branch (X = 0) is
 * already forced to re-rate by the rate control date gate, and an RCD older than X is only bindable
 * under the underwriter hold, where a re-rate would lose the held rate — neither applies. Mirrors
 * charge-policy-step's payment-schedule-date-gate (the authoritative check).
 */
export function getStalePaymentScheduleDate({ option, global = {}, rateControlDate, state, config, now = new Date() }) {
  if (!option || !config?.maxStaleDaysToBind) {
    return null;
  }
  if (isRateControlDateOlderThan({ rateControlDate, state, maxDaysOld: config.maxStaleDaysToBind, now })) {
    return null;
  }
  const billingTodayStr = formatInTimeZone(now, BILLING_TIME_ZONE, 'yyyy-MM-dd');
  const staleScheduleDates = PAYMENT_SCHEDULE_LINES.map((line) => ({ line, schedule: option[line.scheduleKey] }))
    .filter(({ schedule }) => (schedule?.length ?? 0) > 1)
    .map(({ line, schedule }) => ({ line, ratingDay: schedule[0]?.date }))
    .filter(
      ({ line, ratingDay }) =>
        ratingDay &&
        paymentScheduleWouldChange({
          ratingDay,
          bindDay: billingTodayStr,
          effectiveDate: global?.[line.effectiveDate],
          billingDayOfMonth: global?.[line.billingDayOfMonth],
          paymentType: global?.[line.paymentType],
          megaDownPay: global?.[line.megaDownPay]
        })
    )
    .map(({ ratingDay }) => ratingDay)
    .sort();
  return staleScheduleDates[0] ?? null;
}
